import React, { useCallback, useEffect, useRef, useState } from "react";
import globalAxios from "axios";
import { FiAlertTriangle, FiCheckCircle, FiSearch, FiTool } from "react-icons/fi";
import { Modal } from "../../Components/Modal";
import { LoadingSpinner } from "../../Components/LoadingSpinner";
import GatedButton from "../../Components/GatedButton";

/* ------------------------------------------------------------------ */
/*  Server shapes (GET /api/deployment/preview/repair/status)          */
/* ------------------------------------------------------------------ */

export type RepairJobState = "idle" | "running" | "completed" | "failed";

export type RepairJobStatus = {
	state: RepairJobState;
	/** Job name of the maintenance slot ("integrity-scan-production", "repair-preview"...). */
	target: string | null;
	total: number;
	done: number;
	deployed: number;
	skipped: number;
	failed: number;
	error: string | null;
};

export type PreviewRepairIssue = {
	kind: string;
	schemaKey: string | null;
	language: string | null;
	location: string | null;
	contentId: string | null;
	detail: string;
	/** null after a scan; "fixed" or "failed: …" after a repair. */
	outcome: string | null;
};

export type PreviewRepairReport = {
	applied: boolean;
	pagesChecked: number;
	pagesIdentical: number;
	singletonsChecked: number;
	customRecordsChecked: number;
	dynamicRecordsChecked: number;
	dynamicRecordsIdentical: number;
	markerFixes: number;
	redeploys: number;
	orphansRemoved: number;
	fragmentsRebaked: boolean;
	elapsedMs: number;
	issues: PreviewRepairIssue[];
	isClean: boolean;
};

const JOB_STATES: RepairJobState[] = ["idle", "running", "completed", "failed"];

/** The enum arrives as a PascalCase string (Newtonsoft StringEnumConverter); numbers tolerated. */
const normalizeJobState = (raw: unknown): RepairJobState => {
	if (typeof raw === "number" && raw >= 0 && raw < JOB_STATES.length) return JOB_STATES[raw];
	const s = String(raw ?? "").trim().toLowerCase();
	return (JOB_STATES as string[]).includes(s) ? (s as RepairJobState) : "idle";
};

export const normalizeJobStatus = (raw: any): RepairJobStatus => ({
	state: normalizeJobState(raw?.state),
	target: raw?.target != null ? String(raw.target) : null,
	total: Number(raw?.total ?? 0),
	done: Number(raw?.done ?? 0),
	deployed: Number(raw?.deployed ?? 0),
	skipped: Number(raw?.skipped ?? 0),
	failed: Number(raw?.failed ?? 0),
	error: raw?.error ?? null
});

const normalizeIssue = (raw: any): PreviewRepairIssue => ({
	kind: String(raw?.kind ?? ""),
	schemaKey: raw?.schemaKey ?? null,
	language: raw?.language ?? null,
	location: raw?.location ?? null,
	contentId: raw?.contentId ?? null,
	detail: String(raw?.detail ?? ""),
	outcome: raw?.outcome ?? null
});

export const normalizeReport = (raw: any): PreviewRepairReport | null => {
	if (!raw || typeof raw !== "object") return null;
	const issues = Array.isArray(raw.issues) ? raw.issues.map(normalizeIssue) : [];
	return {
		applied: raw.applied === true,
		pagesChecked: Number(raw.pagesChecked ?? 0),
		pagesIdentical: Number(raw.pagesIdentical ?? 0),
		singletonsChecked: Number(raw.singletonsChecked ?? 0),
		customRecordsChecked: Number(raw.customRecordsChecked ?? 0),
		dynamicRecordsChecked: Number(raw.dynamicRecordsChecked ?? 0),
		dynamicRecordsIdentical: Number(raw.dynamicRecordsIdentical ?? 0),
		markerFixes: Number(raw.markerFixes ?? 0),
		redeploys: Number(raw.redeploys ?? 0),
		orphansRemoved: Number(raw.orphansRemoved ?? 0),
		fragmentsRebaked: raw.fragmentsRebaked === true,
		elapsedMs: Number(raw.elapsedMs ?? 0),
		issues,
		isClean: raw.isClean === true || issues.length === 0
	};
};

/* ------------------------------------------------------------------ */
/*  French labels                                                      */
/* ------------------------------------------------------------------ */

export const KIND_LABELS: Record<string, string> = {
	"page-missing": "Pages manquantes",
	"page-mismatch": "Pages différentes de la source",
	"record-missing": "Enregistrements manquants",
	"record-mismatch": "Contenus différents de la source",
	"orphan-record": "Enregistrements orphelins",
	"orphan-file": "Fichiers orphelins",
	"slug-collision": "Collisions de chemin",
	"template-duplicate": "Gabarits en double (avertissement)",
	marker: "Marqueurs à corriger"
};

export const kindLabelFr = (kind: string): string => {
	if (KIND_LABELS[kind]) return KIND_LABELS[kind];
	const s = kind.replace(/[-_]+/g, " ").trim();
	return s ? s.charAt(0).toUpperCase() + s.slice(1) : "Divergences";
};

const elapsedFr = (ms: number): string => (ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${ms} ms`);

/** Small "value + label" counter used by the report summary. */
const Counter: React.FC<{ value: React.ReactNode; label: string }> = ({ value, label }) => (
	<div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center">
		<div className="text-sm font-bold text-slate-800">{value}</div>
		<div className="text-[10px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
	</div>
);

/** « corrigé » / « échec » chip on a repaired issue; nothing after a plain scan. */
export const OutcomeChip: React.FC<{ outcome: string | null }> = ({ outcome }) => {
	if (!outcome) return null;
	if (outcome === "queued") {
		return (
			<span
				className="inline-flex shrink-0 items-center rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[11px] font-semibold text-sky-700"
				title="Ce contenu attend une publication editoriale : la correction ne le touche pas (voir la file de publication)."
			>
				en file
			</span>
		);
	}
	const failed = outcome.toLowerCase().startsWith("failed");
	return (
		<span
			className={`inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
				failed ? "border-rose-200 bg-rose-50 text-rose-700" : "border-emerald-200 bg-emerald-50 text-emerald-700"
			}`}
			title={failed ? outcome : undefined}
		>
			{failed ? "échec" : "corrigé"}
		</span>
	);
};

/** One kind of divergence with its issues (location + detail + outcome after repair). */
const IssueGroup: React.FC<{ kind: string; issues: PreviewRepairIssue[] }> = ({ kind, issues }) => (
	<div className="overflow-hidden rounded-xl border border-slate-200">
		<div className="flex items-center justify-between gap-2 border-b border-slate-100 bg-slate-50/80 px-3 py-2">
			<span className="text-xs font-semibold uppercase tracking-wide text-slate-600">{kindLabelFr(kind)}</span>
			<span className="rounded-full bg-white px-2 py-0.5 text-xs font-semibold text-slate-600 shadow-sm">{issues.length}</span>
		</div>
		<ul className="divide-y divide-slate-100 bg-white">
			{issues.map((issue, i) => (
				<li key={`${issue.kind}-${issue.location ?? issue.contentId ?? i}`} className="flex items-start gap-3 px-3 py-2">
					<div className="min-w-0 flex-1">
						<p className="truncate font-mono text-xs text-slate-800" title={issue.location ?? issue.contentId ?? ""}>
							{issue.location ?? issue.contentId ?? "-"}
							{issue.language && <span className="ml-1.5 font-sans text-[11px] text-slate-400">({issue.language})</span>}
						</p>
						<p className="mt-0.5 text-xs text-slate-500">{issue.detail}</p>
					</div>
					<OutcomeChip outcome={issue.outcome} />
				</li>
			))}
		</ul>
	</div>
);

/* ------------------------------------------------------------------ */
/*  Section                                                            */
/* ------------------------------------------------------------------ */

/**
 * « Réparer la preview » : the two-phase repair of the preview read stores:
 * 1. « Analyser la preview » (dry run, writes nothing) → readable report;
 * 2. when divergences are found, « Réparer (N corrections) » applies each fix through the
 *    canonical publish pipeline (redeploy, marker fix, orphan removal) : never a direct
 *    read-store write. Both phases run server-side; this section polls the status endpoint
 *    while a job runs and renders the report when it completes.
 */
const PreviewRepairSection: React.FC<{ pollMs?: number }> = ({ pollMs = 2500 }) => {
	// Which read store the integrity scan compares against. Production is scan-only (never-purge);
	// only preview offers the in-place repair.
	const [target, setTarget] = useState<"preview" | "production">("preview");
	const targetLabel = target === "production" ? "la production" : "la preview";
	const [status, setStatus] = useState<RepairJobStatus | null>(null);
	const [report, setReport] = useState<PreviewRepairReport | null>(null);
	const [running, setRunning] = useState(false);
	const [starting, setStarting] = useState(false);
	// Which phase THIS session launched : drives the running label (a resumed job defaults to scan).
	const [phase, setPhase] = useState<"scan" | "repair" | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showConfirm, setShowConfirm] = useState(false);
	const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
	const mountedRef = useRef(true);

	const stopPolling = useCallback(() => {
		if (timerRef.current) {
			clearInterval(timerRef.current);
			timerRef.current = null;
		}
	}, []);

	const fetchStatus = useCallback(async (): Promise<boolean> => {
		try {
			const res = await globalAxios.get(`/api/deployment/preview/repair/status`, {
				withCredentials: true,
				headers: { Accept: "application/json" }
			});
			if (!mountedRef.current) return false;
			const d = res.data ?? {};
			const nextStatus = normalizeJobStatus(d.status);
			const isRunning = d.running === true || nextStatus.state === "running";
			setStatus(nextStatus);
			setReport(normalizeReport(d.report));
			setRunning(isRunning);
			return isRunning;
		} catch (err) {
			console.error(err);
			// A transient status-fetch failure must not kill the polling loop.
			return true;
		}
	}, []);

	const startPolling = useCallback(() => {
		stopPolling();
		timerRef.current = setInterval(async () => {
			const stillRunning = await fetchStatus();
			if (!stillRunning) stopPolling();
		}, pollMs);
	}, [fetchStatus, pollMs, stopPolling]);

	// On mount: read the last known status/report; resume polling if a job is already running.
	useEffect(() => {
		mountedRef.current = true;
		(async () => {
			const isRunning = await fetchStatus();
			if (isRunning && mountedRef.current) startPolling();
		})();
		return () => {
			mountedRef.current = false;
			stopPolling();
		};
	}, [fetchStatus, startPolling, stopPolling]);

	const start = useCallback(
		async (which: "scan" | "repair") => {
			setStarting(true);
			setError(null);
			try {
				// Scan goes through the per-target integrity endpoint (read-only for production).
				// Repair is preview-only.
				const url =
					which === "scan"
						? `/api/deployment/${target}/integrity-scan`
						: `/api/deployment/preview/repair`;
				await globalAxios.post(url, null, { withCredentials: true, headers: { Accept: "application/json" } });
				setPhase(which);
				setRunning(true);
				await fetchStatus();
				startPolling();
			} catch (err: any) {
				console.error(err);
				if (err?.response?.status === 409) {
					setError(String(err.response?.data?.error ?? "Une opération de maintenance est déjà en cours."));
				} else {
					setError(which === "scan" ? "Impossible de lancer l'analyse." : "Impossible de lancer la réparation.");
				}
			} finally {
				setStarting(false);
			}
		},
		[fetchStatus, startPolling, target]
	);

	const jobFailed = !running && status?.state === "failed";
	const showReport = !running && !jobFailed && report != null;
	const fixCount = report && !report.applied ? report.issues.length : 0;

	// Group issues by kind, keeping the server's order of first appearance.
	const groups: Array<{ kind: string; issues: PreviewRepairIssue[] }> = [];
	if (showReport && report) {
		const byKind = new Map<string, PreviewRepairIssue[]>();
		report.issues.forEach((issue) => {
			const bucket = byKind.get(issue.kind);
			if (bucket) bucket.push(issue);
			else {
				const next: PreviewRepairIssue[] = [issue];
				byKind.set(issue.kind, next);
				groups.push({ kind: issue.kind, issues: next });
			}
		});
	}

	const recordsChecked = (report?.customRecordsChecked ?? 0) + (report?.dynamicRecordsChecked ?? 0);

	return (
		<section>
			<div className="mb-2 flex flex-wrap items-center justify-between gap-3">
				<h4 className="text-xs font-semibold uppercase tracking-wide text-slate-500">Intégrité Cloudflare</h4>
				<div className="flex items-center gap-2">
					<div className="inline-flex overflow-hidden rounded-lg border border-slate-200 text-xs font-semibold">
						{(["preview", "production"] as const).map((t) => (
							<button
								key={t}
								type="button"
								onClick={() => setTarget(t)}
								disabled={running || starting}
								className={`px-3 py-1.5 transition ${
									target === t ? "bg-[#005CA9] text-white" : "bg-white text-slate-600 hover:bg-slate-50"
								} disabled:cursor-not-allowed disabled:opacity-60`}
							>
								{t === "preview" ? "Preview" : "Production"}
							</button>
						))}
					</div>
					<button
						type="button"
						onClick={() => start("scan")}
						disabled={running || starting}
						className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60"
					>
						{starting || running ? <LoadingSpinner size="sm" inline /> : <FiSearch className="h-3.5 w-3.5" />}
						Analyser {targetLabel}
					</button>
				</div>
			</div>
			<p className="mb-3 text-xs text-slate-500">
				Compare l'intégralité de la source SQL à {targetLabel} sans rien écrire et signale chaque divergence.
				{target === "preview"
					? " Sur la preview, vous pouvez ensuite corriger chaque écart via le pipeline normal de publication."
					: " La production est analysée en lecture seule ; les corrections passent par la publication normale."}
			</p>

			{error && (
				<p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
					{error}
				</p>
			)}
			{jobFailed && status?.error && (
				<p className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">
					L'opération a échoué : {status.error}
				</p>
			)}

			{running && (
				<div className="rounded-xl border border-sky-200 bg-sky-50/60 px-4 py-3">
					<div className="flex items-center gap-2 text-sm font-medium text-sky-800">
						<LoadingSpinner size="sm" inline />
						{phase === "repair" ? "Réparation en cours…" : "Analyse en cours…"}
						{status && status.total > 0 && (
							<span className="font-normal text-sky-700">
								{status.done}/{status.total}
							</span>
						)}
					</div>
					{status && status.total > 0 && (
						<div className="mt-2 h-1.5 overflow-hidden rounded-full bg-sky-100">
							<div
								className="h-full rounded-full bg-sky-500 transition-all"
								style={{ width: `${Math.min(100, Math.round((status.done / status.total) * 100))}%` }}
							/>
						</div>
					)}
				</div>
			)}

			{showReport && report && (
				<div className="space-y-3">
					{report.isClean ? (
						<div className="rounded-xl border border-emerald-200 bg-emerald-50/70 px-4 py-3">
							<p className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
								<FiCheckCircle /> {target === "production" ? "Production" : "Preview"} conforme à la source ✓
							</p>
							<div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
								<Counter value={report.pagesChecked} label="Pages vérifiées" />
								<Counter value={report.pagesIdentical} label="Pages identiques" />
								<Counter value={recordsChecked} label="Enregistrements" />
								<Counter value={elapsedFr(report.elapsedMs)} label="Durée" />
							</div>
						</div>
					) : (
						<>
							<div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-amber-200 bg-amber-50/70 px-4 py-3">
								<p className="flex items-center gap-2 text-sm font-semibold text-amber-800">
									<FiAlertTriangle />
									{report.applied
										? "Rapport de réparation"
										: `${report.issues.length} divergence${report.issues.length > 1 ? "s" : ""} détectée${report.issues.length > 1 ? "s" : ""}`}
								</p>
								{!report.applied && fixCount > 0 && target === "preview" && (
									<GatedButton
										token="feature:deployment:write"
										onClick={() => setShowConfirm(true)}
										disabled={running || starting}
										className="inline-flex items-center gap-2 rounded-lg bg-[#005CA9] px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition hover:bg-[#004b8a] disabled:cursor-not-allowed disabled:bg-slate-300"
									>
										<FiTool className="h-3.5 w-3.5" /> Réparer ({fixCount} correction{fixCount > 1 ? "s" : ""})
									</GatedButton>
								)}
							</div>
							{report.applied && (
								<div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
									<Counter value={report.redeploys} label="Redéploiements" />
									<Counter value={report.markerFixes} label="Marqueurs corrigés" />
									<Counter value={report.orphansRemoved} label="Orphelins supprimés" />
									<Counter value={report.fragmentsRebaked ? "oui" : "non"} label="Fragments recuits" />
								</div>
							)}
							{groups.map((group) => (
								<IssueGroup key={group.kind} kind={group.kind} issues={group.issues} />
							))}
						</>
					)}
				</div>
			)}

			{/* Repair confirmation */}
			<Modal show={showConfirm} onHide={() => setShowConfirm(false)} size="sm" centered>
				<Modal.Header closeButton>
					<Modal.Title>Réparer la preview ?</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<p className="text-sm text-slate-700">
						{fixCount} correction{fixCount > 1 ? "s" : ""} seront appliquées. Chaque correction passe par le
						pipeline normal de publication (redéploiement, correction de marqueur, suppression d'orphelin) : aucune écriture directe dans les magasins de lecture.
					</p>
				</Modal.Body>
				<Modal.Footer>
					<button
						type="button"
						onClick={() => setShowConfirm(false)}
						className="inline-flex items-center rounded-full border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-slate-300 hover:bg-slate-50"
					>
						Annuler
					</button>
					<button
						type="button"
						onClick={() => {
							setShowConfirm(false);
							start("repair");
						}}
						className="inline-flex items-center gap-2 rounded-full bg-[#005CA9] px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-[#004b8a]"
					>
						<FiTool className="h-4 w-4" /> Lancer la réparation
					</button>
				</Modal.Footer>
			</Modal>
		</section>
	);
};

export default PreviewRepairSection;
